import { ChevronRight } from "lucide-react";
import { DateTime } from "luxon";
import { UserAvatar } from "@/components/ui/stacked-avatars";
import { cn } from "@/lib/utils";
import {
  ShareableEntity,
  ShareableEntitySwap,
} from "@/lib/api/shareable-entities";
import { useBatchSignedMedia } from "@/lib/hooks/use-signed-media";
import { useMemo } from "react";
import {
  SWAP_PROMPTS,
  RANDOM_SWAP_PROMPTS,
  type SwapPrompt,
} from "@somethings-ui/core/src/swap-prompts";

interface SwapCardProps {
  entity: ShareableEntity & { swap: ShareableEntitySwap };
  isNew: boolean;
  onClick?: () => void;
  className?: string;
}

export function SwapCard({ entity, isNew, onClick, className }: SwapCardProps) {
  const isScheduled = entity.status === "scheduled" && entity.scheduledAt;

  const formattedDate = isScheduled
    ? `Scheduled, ${DateTime.fromISO(entity.scheduledAt!).toFormat("MMM d")}`
    : DateTime.fromJSDate(new Date(entity.created_at)).toFormat("EEE, h:mm a");

  const prompt = useMemo(
    () =>
      SWAP_PROMPTS.find((p: SwapPrompt) => p.id === entity.swap.promptId) ??
      RANDOM_SWAP_PROMPTS.find((p: SwapPrompt) => p.id === entity.swap.promptId),
    [entity.swap.promptId],
  );
  const promptTitle =
    entity.swap.promptId === "custom"
      ? (entity.swap.customTitle ?? "Swap")
      : (prompt?.title ?? "Swap");
  const creatorName = entity.owner.name;
  const creatorAvatarUrl = entity.owner.profile_picture_url ?? undefined;

  const { mentorImage, menteeImage, status } = entity.swap;

  const imageUrls = useMemo(() => {
    const urls: string[] = [];
    if (mentorImage?.thumbnailUrl) urls.push(mentorImage.thumbnailUrl);
    if (menteeImage?.thumbnailUrl) urls.push(menteeImage.thumbnailUrl);
    return urls;
  }, [mentorImage, menteeImage]);

  const signedUrls = useBatchSignedMedia(imageUrls);

  const mentorSignedUrl = mentorImage ? signedUrls[0]?.url : undefined;
  const menteeSignedUrl = menteeImage ? signedUrls[mentorImage ? 1 : 0]?.url : undefined;

  const isPendingTeen = status === "pending_teen";
  const isCompleted = status === "completed";

  return (
    <div
      onClick={onClick}
      className={cn(
        "flex cursor-pointer items-center gap-4 rounded-lg p-3 transition-colors hover:bg-gray-50",
        className,
      )}
    >
      {/* Two tilted photo thumbnails */}
      <div className="relative h-20 w-16 shrink-0">
        {/* Back layer (mentee photo if exists) */}
        {menteeSignedUrl ? (
          <div
            className="absolute inset-0 overflow-hidden rounded-md shadow-sm"
            style={{ transform: "rotate(6deg) translateX(4px)" }}
          >
            <img
              src={menteeSignedUrl}
              alt="Teen's swap photo"
              className="h-full w-full object-cover"
            />
          </div>
        ) : (
          <div
            className="absolute inset-0 rounded-md border-2 border-dashed border-gray-300 bg-[#f7f5f1] shadow-sm"
            style={{ transform: "rotate(6deg) translateX(4px)" }}
          />
        )}
        {/* Front layer (mentor photo) */}
        {mentorSignedUrl ? (
          <div
            className="absolute inset-0 overflow-hidden rounded-md shadow-md"
            style={{ transform: "rotate(-4deg)" }}
          >
            <img
              src={mentorSignedUrl}
              alt="Mentor's swap photo"
              className="h-full w-full object-cover"
            />
          </div>
        ) : (
          <div
            className="absolute inset-0 rounded-md border-2 border-dashed border-gray-300 bg-[#f7f5f1] shadow-md"
            style={{ transform: "rotate(-4deg)" }}
          />
        )}
        {/* Avatar overlay */}
        <div className="absolute -bottom-1 -right-1 z-10">
          <UserAvatar
            imageUrl={creatorAvatarUrl}
            name={creatorName}
            size={28}
            showBorder={true}
          />
        </div>
      </div>

      <div className="flex min-w-0 flex-1 flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="font-bold text-primary">Swaps</span>
          <span className="h-1 w-1 rounded-full bg-text-tertiary" />
          <span className="text-sm text-text-tertiary">
            {isScheduled
              ? formattedDate
              : isCompleted
                ? `Completed, ${formattedDate}`
                : "Pending"}
          </span>
        </div>
        <p className="truncate text-sm text-tertiary">{promptTitle}</p>
        {isPendingTeen && (
          <p className="text-xs text-amber-600">Waiting for response...</p>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {(isNew && isCompleted) || !isCompleted ? (
          <span className="h-2 w-2 rounded-full bg-purple-600" />
        ) : null}
        <ChevronRight className="h-5 w-5 text-gray-400" />
      </div>
    </div>
  );
}
